import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { ContentContainer, PageRail, GoogleDFP } from '@surfline/quiver-react';
import SpotReport from '@surfline/quiver-react/dist/SpotReport';
import { loadTreatment } from '@surfline/web-common/dist/features/redux';
import config from '../../../../config';
import loadAdConfig from '../../../../utils/adConfig';
import SpotReportCams from '../../../SpotReportCams';
import { trackEvent } from '../../../../actions/segmentEvents';
import userDetailsPropType from '../../../../propTypes/userDetails';
import { getPremiumStatus, getUserDetails, getUserEntitlements } from '../../../../selectors/user';
import {
  getSpotDetails,
  getSpotReport,
  getSpotReportData,
  getSpotReportAssociated,
  getAdvertisingIds,
  getLegacySpotId,
  getSpotMultiCam,
} from '../../../../selectors/spot';
import { getSubregionAssociated } from '../../../../selectors/subregion';
import CTAProvider from '../../../CTAProvider';
import SpotReportCamHeaderCTA from '../../../../components/SpotReportCamHeaderCTA';
import NearbyCams from '../../../../components/NearbyCams';
import CompanionAd from '../../../../components/CompanionAd';
import PremiumBenefitsCenter from '../../../../components/PremiumBenefitsCenter';
import FeaturedContent from '../../../../components/FeaturedContent';
import featuredContentPropType from '../../../../propTypes/featuredContent';
import multiCamPropType from '../../../../propTypes/multiCam';
import spotReportDataPropType from '../../../../propTypes/spot';
import devicePropType from '../../../../propTypes/device';
import { getPremiumPerks } from '../../../../selectors/promotions';
import { premiumPerksPropType } from '../../../../propTypes/promotions';

const spotCurrentConditionsSectionClass = (
  hasCam,
  hasReport,
  isPremium,
  isMultiCam,
  isTheatreMode
) =>
  classNames({
    'sl-spot-current-conditions-section': true,
    'sl-spot-current-conditions-section--with-cam': hasCam,
    'sl-spot-current-conditions-section--no-cam': !hasCam,
    'sl-spot-current-conditions-section--with-report': hasReport,
    'sl-spot-current-conditions-section--no-report': !hasReport,
    'sl-spot-current-conditions-section--premium': isPremium,
    'sl-spot-current-conditions-section--free': !isPremium,
    'sl-spot-current-conditions-section--multi-cam': isMultiCam,
    'sl-spot-current-conditions-section--single-cam': hasCam && !isMultiCam,
    'sl-spot-current-conditions-section--theatre-mode': isTheatreMode,
  });

const spotModuleClass = (hasCam, isMultiCam) =>
  classNames({
    'sl-spot-module': true,
    'sl-spot-module--with-cam': hasCam,
    'sl-spot-module--no-cam': !hasCam,
    'sl-spot-module--multi-cam': isMultiCam,
  });

const SpotCurrentConditionsSection = ({
  isPremium,
  spot,
  spotId,
  report,
  reportData,
  reportAssociated,
  subregionAssociated,
  doTrackEvent,
  advertisingIds,
  userDetails,
  entitlements,
  adTargets,
  featuredContent,
  multiCam,
  legacySpotId,
  device,
  doLoadTreatment,
  premiumPerks,
}) => {
  const hasCam = spot.cameras.length > 0;
  const hasReport = !!report;
  const note = reportData && reportData.note ? reportData.note : null;
  const isDesktop = device.width >= config.smallDesktopWidth;

  // Logic for (multiCam.isMultiCam || isPremium): Show if multiCam spot
  // or if user is premium giving them access to theatre mode
  const { isMultiCam } = multiCam;
  const multiCamActiveView = multiCam.activeView;
  const isTheatreMode = (isPremium && isMultiCam) || (!isPremium && multiCamActiveView === 'MULTI');
  const isTheatreWithNoReport = isTheatreMode && !hasReport;

  const headerCTASegmentProperties = {
    ctaLocation: 'cam player top',
    category: 'cams & reports',
    pageName: 'Spot Report',
    spotId,
    spotName: spot.name,
  };

  return (
    <div
      className={spotCurrentConditionsSectionClass(
        hasCam,
        hasReport,
        isPremium,
        isMultiCam,
        isTheatreMode
      )}
    >
      {hasCam || hasReport ? null : (
        <ContentContainer>
          <div className="sl-spot-current-conditions-section__ad-top-banner">
            <GoogleDFP
              unifiedAdMarketplace
              adConfig={loadAdConfig('spotNoReportOrCamTopBanner', adTargets, entitlements)}
            />
          </div>
        </ContentContainer>
      )}
      {hasCam && isTheatreMode && (
        <>
          {!isPremium && (
            <div className="sl-spot-report-cam__cta">
              <CTAProvider segmentProperties={headerCTASegmentProperties}>
                <SpotReportCamHeaderCTA />
              </CTAProvider>
            </div>
          )}
          <div className="sl-spot-current-conditions-section__theatre-cam">
            <ContentContainer>
              <SpotReportCams
                doLoadTreatment={doLoadTreatment}
                spotId={spotId}
                legacySpotId={legacySpotId}
                spot={spot}
                reportData={reportData}
                userDetails={userDetails}
                isPremium={isPremium}
                isTheatreMode={isTheatreMode}
                headerCTASegmentProperties={headerCTASegmentProperties}
                isMultiCam={isMultiCam}
              />
            </ContentContainer>
          </div>
          <ContentContainer>
            <NearbyCams spotId={spotId} />
          </ContentContainer>
        </>
      )}
      {isTheatreWithNoReport && (
        <ContentContainer>
          <div className="sl-spot-current-conditions-section__ad-top-banner">
            <GoogleDFP
              unifiedAdMarketplace
              adConfig={loadAdConfig('spotNoReportOrCamTopBanner', adTargets, entitlements)}
            />
          </div>
        </ContentContainer>
      )}
      <ContentContainer>
        <PageRail side="left">
          {hasCam && !isTheatreMode && (
            <SpotReportCams
              doLoadTreatment={doLoadTreatment}
              spotId={spotId}
              legacySpotId={legacySpotId}
              spot={spot}
              reportData={reportData}
              userDetails={userDetails}
              isPremium={isPremium}
              isTheatreMode={isTheatreMode}
              headerCTASegmentProperties={headerCTASegmentProperties}
              isMultiCam={isMultiCam}
              entitlements={entitlements}
            />
          )}
          {((hasCam && isTheatreMode && isDesktop) || !hasCam) && (
            <div className={spotModuleClass(hasCam, isMultiCam)}>
              <SpotReport
                spot={spot}
                note={note}
                humanReport={hasReport ? report : null}
                readings={reportData}
                associated={reportAssociated}
                subregionAssociated={subregionAssociated}
                doTrackEvent={doTrackEvent}
                adTargets={adTargets}
                userDetails={userDetails}
                entitlements={entitlements}
                isMultiCam={isMultiCam}
                baseUrl={config.homepageUrl}
              />
            </div>
          )}
        </PageRail>
        <PageRail side="right">
          {(hasCam || hasReport) && !isTheatreWithNoReport ? (
            <div className="sl-spot-current-conditions-section__companion-ad">
              <CompanionAd spotId={advertisingIds.spotId} entitlements={entitlements} />
            </div>
          ) : null}
          {premiumPerks && premiumPerks.perks && (
            <PremiumBenefitsCenter
              adTargets={adTargets}
              userDetails={userDetails}
              isPremium={isPremium}
              doTrackEvent={doTrackEvent}
              premiumPerks={premiumPerks}
              doLoadTreatment={doLoadTreatment}
              spotId={spotId}
              spotName={spot.name}
            />
          )}
          <FeaturedContent
            featuredContent={featuredContent}
            isPremium={isPremium}
            doTrackEvent={doTrackEvent}
            spotName={spot.name}
            spotId={spotId}
          />
        </PageRail>
      </ContentContainer>
      <ContentContainer>
        {((hasCam && !isTheatreMode) || (!isDesktop && isMultiCam)) && (
          <SpotReport
            spot={spot}
            note={note}
            humanReport={hasReport ? report : null}
            readings={reportData}
            entitlements={entitlements}
            associated={reportAssociated}
            subregionAssociated={subregionAssociated}
            doTrackEvent={doTrackEvent}
            adTargets={adTargets}
            isMultiCam={isMultiCam}
          />
        )}
        <div className="sl-spot-current-conditions-section__middle-ad">
          <GoogleDFP
            unifiedAdMarketplace
            adConfig={loadAdConfig('spotMiddle', adTargets, entitlements)}
          />
        </div>
      </ContentContainer>
    </div>
  );
};

SpotCurrentConditionsSection.propTypes = {
  isPremium: PropTypes.bool.isRequired,
  doTrackEvent: PropTypes.func.isRequired,
  report: PropTypes.shape(),
  reportData: spotReportDataPropType,
  spot: PropTypes.shape({
    name: PropTypes.string,
    subregion: PropTypes.shape({
      _id: PropTypes.string,
      forecastStatus: PropTypes.string,
    }),
    breadcrumb: PropTypes.array,
    cameras: PropTypes.array,
  }),
  spotId: PropTypes.string,
  entitlements: PropTypes.arrayOf(PropTypes.string),
  reportAssociated: PropTypes.shape({
    utcOffset: PropTypes.number,
  }).isRequired,
  subregionAssociated: PropTypes.shape({
    abbrTimezone: PropTypes.string,
  }).isRequired,
  advertisingIds: PropTypes.shape({
    spotId: PropTypes.string,
    subregionId: PropTypes.string,
  }).isRequired,
  userDetails: userDetailsPropType,
  multiCam: multiCamPropType.isRequired,
  legacySpotId: PropTypes.string,
  adTargets: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  featuredContent: featuredContentPropType,
  device: devicePropType.isRequired,
  doLoadTreatment: PropTypes.func.isRequired,
  premiumPerks: premiumPerksPropType.isRequired,
};

SpotCurrentConditionsSection.defaultProps = {
  userDetails: null,
  reportData: null,
  spot: null,
  spotId: null,
  report: null,
  entitlements: [],
  adTargets: [],
  legacySpotId: null,
  featuredContent: null,
};

export default connect(
  state => ({
    userDetails: getUserDetails(state),
    entitlements: getUserEntitlements(state),
    isPremium: getPremiumStatus(state),
    spot: getSpotDetails(state),
    multiCam: getSpotMultiCam(state),
    legacySpotId: getLegacySpotId(state),
    report: getSpotReport(state),
    advertisingIds: getAdvertisingIds(state),
    reportData: getSpotReportData(state),
    reportAssociated: getSpotReportAssociated(state),
    subregionAssociated: getSubregionAssociated(state),
    premiumPerks: getPremiumPerks(state),
  }),
  dispatch => ({
    doTrackEvent: (event, properties) => dispatch(trackEvent(event, properties)),
    doLoadTreatment: treatment => dispatch(loadTreatment(treatment)),
  })
)(SpotCurrentConditionsSection);
